import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { formatDateTime } from '../../lib/utils';
import Button from './Button';
import NotificationsDropdown from './NotificationsDropdown';

interface NavbarProps {
    title?: string;
    onMenuToggle?: () => void;
    showMenuButton?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({
    title = 'CompilaTime',
    onMenuToggle,
    showMenuButton = false,
}) => {
    const { user, company, logout } = useAuth();
    const { isDarkMode, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const { navigateToEmployeePortal, secureNavigate } = useSecureNavigation();
    const [showUserMenu, setShowUserMenu] = useState(false);
    const userMenuRef = useRef<HTMLDivElement>(null);

    // Cerrar menú cuando se hace clic fuera
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = async () => {
        try {
                        logout();
                        if (company?.slug) {
                navigateToEmployeePortal(company.slug);
            } else {
                                navigate('/portal/login');
            }
        } catch (error) {
            console.error('Navbar: Error al cerrar sesión:', error);
        }
    };

    return (
        <nav className="bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    {/* Izquierda: Logo y título */}
                    <div className="flex items-center">
                        {showMenuButton && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={onMenuToggle}
                                className="mr-4 lg:hidden"
                                icon={
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                    </svg>
                                }
                            />
                        )}

                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                                    <span className="text-white font-bold text-sm">CT</span>
                                </div>
                            </div>
                            <div className="ml-3">
                                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h1>
                                {company && (
                                    <p className="text-sm text-gray-500 dark:text-gray-400">{company.name}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Derecha: Usuario y acciones */}
                    <div className="flex items-center space-x-4">
                        {/* Botón de cambio de tema */}
                        <button
                            onClick={toggleTheme}
                            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 transition-colors"
                            title={isDarkMode ? 'Modo claro' : 'Modo oscuro'}
                        >
                            {isDarkMode ? (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                            ) : (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                </svg>
                            )}
                        </button>

                        {/* Botón de cerrar sesión siempre visible */}
                        <Button
                            onClick={handleLogout}
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900/20"
                            icon={
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4 4m4-4H4m16 0v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1m-6 0h.01M9 16h.01" />
                                </svg>
                            }
                        >
                            Cerrar sesión
                        </Button>

                        {/* Notificaciones */}
                        <NotificationsDropdown />

                        {/* Menú de usuario */}
                        <div className="relative" ref={userMenuRef}>
                            <button
                                onClick={() => setShowUserMenu(!showUserMenu)}
                                className="flex items-center space-x-3 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                                <div className="text-right hidden sm:block">
                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                        {user?.name}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {user?.role}
                                    </div>
                                </div>

                                <div className="relative">
                                    <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
                                        <span className="text-sm font-medium text-gray-700 dark:text-white">
                                            {user?.name?.charAt(0).toUpperCase()}
                                        </span>
                                    </div>
                                </div>
                            </button>

                            {/* Dropdown menu */}
                            {showUserMenu && (
                                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50">
                                    <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">{user?.name}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                            Último acceso: {user?.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'Nunca'}
                                        </p>
                                    </div>

                                    <a
                                        href="#"
                                        className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            secureNavigate('/portal/profile');
                                        }}
                                    >
                                        Mi perfil
                                    </a>
                                    <a
                                        href="#"
                                        className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            secureNavigate('/portal/settings');
                                        }}
                                    >
                                        Configuración
                                    </a>
                                    <div className="border-t border-gray-100 dark:border-gray-700">
                                        <button
                                            onClick={handleLogout}
                                            className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                                        >
                                            Cerrar sesión
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;